import { useEffect, useState } from "react";
import Folder from "../templates/folder/Folder";
import Hr from "../Hr";
import DynamicIcon from "../DynamicIcon";
import { getSystemDetails, selectSystemDetailsData, selectSystemDetailsError, selectSystemDetailsIsLoading } from "../../redux/systems/systemDetailsSlice";
import LoadingSection from "../LoadingSection";
import Error from '../Error';
import { useDispatch, useSelector } from 'react-redux';
import SystemServicesView from './SystemServicesView';
import { setSwap, swapModal, closeModal } from "../../redux/rightModalSlice";
import axios from 'axios'; // Importo axios per le chiamate API
import { selectAccountData } from "../../redux/main/accountSlice"; // Importo per accountId
import { useColorPalette } from '../../hooks/useColorPalette'; // Importa il custom hook
import AssistanceCard from "./AssistanceCard";
import JobCard from "./JobCard";
import MaintenanceCard from "./MaintenanceCard";

// Questo componente (mirrorabile) mostra i dettagli del System e ospita la Folder per visualizzarne i collegamenti

// Poi sfrutta Folder per generare le Card di collegamento
export default function ViewSystem({ systemId }) {
    const accountData = useSelector(selectAccountData);
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
    const dispatch = useDispatch();
    const systemDetailsData = useSelector(selectSystemDetailsData);
    const systemDetailsIsLoading = useSelector(selectSystemDetailsIsLoading);
    const systemDetailsError = useSelector(selectSystemDetailsError);
    const token = localStorage.getItem('installamelo-auth-token');
    const palette = useColorPalette(); // Usa l'hook per ottenere la palette

    // Stati per assistenze, commesse e manutenzioni
    const [assistanceRequests, setAssistanceRequests] = useState([]);
    const [assistanceRequestsLoading, setAssistanceRequestsLoading] = useState(false);
    const [assistanceRequestsError, setAssistanceRequestsError] = useState(null);

    const [jobs, setJobs] = useState([]);
    const [jobsLoading, setJobsLoading] = useState(false);
    const [jobsError, setJobsError] = useState(null);

    const [maintenances, setMaintenances] = useState([]);
    const [maintenancesLoading, setMaintenancesLoading] = useState(false);
    const [maintenancesError, setMaintenancesError] = useState(null);

    useEffect(() => {
        console.log('ViewSystem montato con systemId:', systemId);
        
        if (systemId && accountData?._id && token) {
            console.log('Chiamata getSystemDetails con ID:', systemId);
            dispatch(getSystemDetails({ systemId, token }));

            // Funzione per recuperare i dati
            const fetchData = async (endpoint, setData, setLoading, setError) => {
                setLoading(true);
                setError(null);
                try {
                    const response = await axios.get(`${apiBaseUrl}/${endpoint}`,
                        {
                            headers: { 'auth-token': token },
                            params: { accountId: accountData._id, systemId }
                        });
                    setData(response.data.data || response.data); // Adatto in base alla struttura della risposta
                } catch (err) {
                    setError(err.message || 'Errore nel recupero dati');
                    console.error(`Errore recupero ${endpoint}:`, err);
                }
                setLoading(false);
            };

            // Recupero assistenze
            fetchData('assistanceRequests/list', setAssistanceRequests, setAssistanceRequestsLoading, setAssistanceRequestsError);
            // Recupero commesse solo per il systemId attuale
            fetchData('jobs/list', setJobs, setJobsLoading, setJobsError);
            // Recupero manutenzioni
            fetchData('maintenances/list', setMaintenances, setMaintenancesLoading, setMaintenancesError);
        }
    }, [systemId, dispatch, token, accountData, apiBaseUrl]);
    // Titoli tab con conteggio elementi
    const tabTitles = [
        `ASSISTENZE (${Array.isArray(assistanceRequests) ? assistanceRequests.length : 0})`,
        `COMMESSE (${Array.isArray(jobs) ? jobs.length : 0})`,
        `MANUTENZIONI (${Array.isArray(maintenances) ? maintenances.length : 0})`
    ];
    return (
        <div className="p-8 flex flex-col 2xl:flex-row gap-4">
            {
                systemDetailsIsLoading &&
                <LoadingSection isFixed={false} />
            }
            {
                systemDetailsError &&
                <div className="flex-1/3">
                    <Error message={systemDetailsError} />
                </div>
            }
            {
                /* AREA DETTAGLI */
                systemDetailsData &&
                (
                    <div className="bg-white shadow-xl rounded-lg p-6 flex flex-col gap-6 w-full 2xl:w-1/3 h-fit">
                        {/* Sezione Dettagli Impianto */}
                        <div className="space-y-3">
                            <div className="flex items-center gap-3">
                                <DynamicIcon iconClass={`fa-solid fa-industry text-xl`} style={{ color: palette.iconPrimary }} />
                                <h3 className="text-xl font-semibold" style={{ color: palette.primary }}>Dettagli Impianto</h3>
                            </div>
                            <Hr />
                            <div className="space-y-2 pl-2">
                                <div className="flex items-start gap-3">
                                    <DynamicIcon iconClass="fa-solid fa-user mt-1" style={{ color: palette.iconDefault }} />
                                    <div>
                                        <p className="text-sm font-medium" style={{ color: palette.inputText }}>Cliente</p>
                                        <p className="text-md font-semibold" style={{ color: palette.primary }}>{systemDetailsData?.customerLabel}</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <DynamicIcon iconClass="fa-solid fa-tag mt-1" style={{ color: palette.iconDefault }} />
                                    <div>
                                        <p className="text-sm font-medium" style={{ color: palette.inputText }}>Nome Impianto</p>
                                        <p className="text-md font-semibold" style={{ color: palette.primary }}>{systemDetailsData?.title}</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <DynamicIcon iconClass="fa-solid fa-location-dot mt-1" style={{ color: palette.iconDefault }} />
                                    <div>
                                        <p className="text-sm font-medium" style={{ color: palette.inputText }}>Indirizzo</p>
                                        <p className="text-md" style={{ color: palette.primary }}>{systemDetailsData?.customerAddressLabel}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Sezione Tipi di Impianto */}
                        <div className="space-y-3">
                            <div className="flex items-center gap-3">
                                <DynamicIcon iconClass="fa-solid fa-concierge-bell text-xl" style={{ color: palette.iconPrimary }} />
                                <h3 className="text-xl font-semibold" style={{ color: palette.primary }}>Tipi di Impianto</h3>
                            </div>
                            <Hr />
                            <div className="pl-2">
                                <SystemServicesView 
                                    servicesArray={systemDetailsData.typesList} 
                                    headerBgColor={palette.tableHeaderBg}
                                    headerTextColor={palette.tableHeaderText}
                                    rowLightBgColor={palette.tableRowLight}
                                    rowLightTextColor={palette.textOnTableRowLight}
                                    rowDarkBgColor={palette.tableRowDark}
                                    rowDarkTextColor={palette.textOnTableRowDark}
                                    highlightBgColor={palette.highlightBg}
                                    highlightTextColor={palette.textOnHighlight}
                                />
                            </div>
                        </div>

                        {/* Azioni - Pulsanti Chiudi e Modifica */}
                        <div className="mt-auto pt-4">
                            <div className="flex justify-end gap-3">
                                {/* Pulsante Chiudi */}
                                <button
                                    onClick={() => {
                                        dispatch(closeModal());
                                    }}
                                    className="flex-shrink-0 px-4 py-2 rounded"
                                    style={{
                                        backgroundColor: palette.buttonSecondaryBg || '#6c757d',
                                        color: palette.buttonSecondaryText || '#ffffff',
                                        border: `1px solid ${palette.buttons?.secondary?.border || 'transparent'}`
                                    }}
                                    onMouseOver={(e) => {
                                        e.currentTarget.style.backgroundColor = palette.buttonSecondaryHover || '#5a6268';
                                    }}
                                    onMouseOut={(e) => {
                                        e.currentTarget.style.backgroundColor = palette.buttonSecondaryBg || '#6c757d';
                                    }}
                                >
                                    <DynamicIcon iconClass="fa-solid fa-xmark mr-2" style={{ color: palette.buttonSecondaryText || '#ffffff' }} />
                                    Chiudi
                                </button>
                                
                                {/* Pulsante Modifica Impianto */}
                                <button
                                    onClick={() => {
                                        dispatch(setSwap({
                                            mode: 'edit',
                                            id: systemId,
                                            percentuageWidth: 50,
                                            isOld: true
                                        }));
                                    }}
                                    className="flex-shrink-0 text-center px-4 py-2 rounded"
                                    style={{
                                        backgroundColor: palette.buttonPrimaryBg || palette.primary,
                                        color: palette.buttonPrimaryText || palette.textOnPrimary,
                                        border: `1px solid ${palette.buttons?.primary?.border || 'transparent'}`
                                    }}
                                    onMouseOver={(e) => {
                                        e.currentTarget.style.backgroundColor = palette.buttonPrimaryHover || palette.primary;
                                    }}
                                    onMouseOut={(e) => {
                                        e.currentTarget.style.backgroundColor = palette.buttonPrimaryBg || palette.primary;
                                    }}
                                >
                                    <DynamicIcon iconClass="fa-solid fa-pencil mr-2" style={{ color: palette.buttonPrimaryText || palette.textOnPrimary }} />
                                    Modifica Impianto
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* CARTELLE NAVIGABILI */}
            <div className="flex-1">
                <Folder tabTitleArray={tabTitles}>
                {/* Qui andranno i componenti per visualizzare le liste, per ora solo un placeholder */}
                <div>
                    {assistanceRequestsLoading && <p>Caricamento assistenze...</p>}
                    {assistanceRequestsError && <Error message={assistanceRequestsError} />}
                    {assistanceRequests && assistanceRequests.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {assistanceRequests.map(req => (
                                <AssistanceCard
                                    key={req._id}
                                    assistance={req}
                                    accountId={accountData?._id}
                                    token={token}
                                    apiBaseUrl={apiBaseUrl}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-2" style={{ color: palette.iconDefault }}>Nessuna assistenza collegata a questo impianto.</div>
                    )}
                </div>
                <div>
                    {jobsLoading && <p>Caricamento commesse...</p>}
                    {jobsError && <Error message={jobsError} />}
                    {jobs && jobs.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {jobs.map(job => (
                                <JobCard
                                    key={job._id}
                                    job={job}
                                    accountId={accountData?._id}
                                    token={token}
                                    apiBaseUrl={apiBaseUrl}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="text-gray-500 text-center py-2">Nessuna commessa collegata a questo impianto.</div>
                    )}
                </div>
                <div>
                    {maintenancesLoading && <p>Caricamento manutenzioni...</p>}
                    {maintenancesError && <Error message={maintenancesError} />}
                    {maintenances && maintenances.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {maintenances.map(m => (
                                <MaintenanceCard
                                    key={m._id}
                                    maintenance={m}
                                    accountId={accountData?._id}
                                    token={token}
                                    apiBaseUrl={apiBaseUrl}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="text-gray-500 text-center py-2">Nessuna manutenzione collegata a questo impianto.</div>
                    )}
                </div>
                <div>
                    {/* Placeholder per Rapportini */}
                    <p>Contenuto Rapportini (da implementare)</p>
                </div>
                </Folder>
            </div>
        </div>
    )
}